import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { NotificationType, Campaign, PhoneNumber } from '@/types'

export const useAppStore = defineStore('app', () => {
  // State
  const isLoading = ref(false)
  const notification = ref<NotificationType | null>(null)
  const selectedCampaign = ref<Campaign | null>(null)
  const selectedPhoneNumber = ref<PhoneNumber | null>(null)

  // Actions
  const setLoading = (loading: boolean): void => {
    isLoading.value = loading
  }

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    duration: number = 5000
  ): void => {
    notification.value = { message, type }

    // Auto-clear notification after duration
    if (duration > 0) {
      setTimeout(() => {
        clearNotification()
      }, duration)
    }
  }

  const clearNotification = (): void => {
    notification.value = null
  }

  const setSelectedCampaign = (campaign: Campaign | null): void => {
    selectedCampaign.value = campaign
    // Store in localStorage for persistence
    if (campaign) {
      localStorage.setItem('ballotshift_selected_campaign', JSON.stringify(campaign))
    } else {
      localStorage.removeItem('ballotshift_selected_campaign')
    }
  }

  const setSelectedPhoneNumber = (phoneNumber: PhoneNumber | null): void => {
    selectedPhoneNumber.value = phoneNumber
    // Store in localStorage for persistence
    if (phoneNumber) {
      localStorage.setItem('ballotshift_selected_phone', JSON.stringify(phoneNumber))
    } else {
      localStorage.removeItem('ballotshift_selected_phone')
    }
  }

  const initializeApp = (): void => {
    try {
      // Restore selected campaign
      const storedCampaign = localStorage.getItem('ballotshift_selected_campaign')
      if (storedCampaign) {
        selectedCampaign.value = JSON.parse(storedCampaign)
      }

      // Restore selected phone number
      const storedPhone = localStorage.getItem('ballotshift_selected_phone')
      if (storedPhone) {
        selectedPhoneNumber.value = JSON.parse(storedPhone)
      }
    } catch (error) {
      console.error('Error initializing app state:', error)
      // Clear invalid stored data
      localStorage.removeItem('ballotshift_selected_campaign')
      localStorage.removeItem('ballotshift_selected_phone')
    }
  }

  const resetAppState = (): void => {
    isLoading.value = false
    notification.value = null
    selectedCampaign.value = null
    selectedPhoneNumber.value = null
    
    // Clear localStorage
    localStorage.removeItem('ballotshift_selected_campaign')
    localStorage.removeItem('ballotshift_selected_phone')
  }

  // Initialize app state on store creation
  initializeApp()

  return {
    // State
    isLoading,
    notification,
    selectedCampaign,
    selectedPhoneNumber,
    
    // Actions
    setLoading,
    showNotification,
    clearNotification,
    setSelectedCampaign,
    setSelectedPhoneNumber,
    initializeApp,
    resetAppState,
  }
})

